import { StyledMultiSelect } from '@features/reports/components/monitoringReports/DashboardFilters/BasicFilters'
import type { HomeDto, ShiftType, VisitType } from '@lmc/ts-common'
import { Button, FormItem, GroupedUsersMultiSelect } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Divider, Modal } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { RotaOverviewFilters } from 'screens/Rostering/hooks/useRotaOverview'
import { useShiftTypesList } from 'screens/Rostering/hooks/useShiftTypesList'
import useUsersList from 'screens/Rostering/hooks/useUsersList'
import { useVisitTypesList } from 'screens/Rostering/hooks/useVisitTypesList'
import styled from 'styled-components'
import { useExportRotaOverviewCsv } from '../../hooks/useExportRotaOverview'
import DownloadFileIcon from './DownloadFileIcon'
import WeekRangePicker from './WeekRangePicker'

interface DownloadRotaOverviewModalProps {
  isVisible: boolean
  onClose: () => void
  initialFilters: RotaOverviewFilters
}

const DownloadRotaOverviewModal = ({
  isVisible,
  onClose,
  initialFilters
}: DownloadRotaOverviewModalProps) => {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<RotaOverviewFilters>(initialFilters)

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const { users } = useUsersList({})
  const exportCsv = useExportRotaOverviewCsv(filters)

  const { shiftTypes } = useShiftTypesList()
  const { visitTypes } = useVisitTypesList()

  const contractTypeOptions =
    home.rotaSettings?.contractTypes?.map((contractType) => ({
      label: contractType.name,
      value: contractType.id
    })) || []

  const visitOptions =
    visitTypes?.map((visit: VisitType) => ({
      label: `${visit.name} Visit`,
      value: visit.id
    })) || []

  const shiftOptions =
    shiftTypes?.map((shift: ShiftType) => ({
      label: `${shift.name} Shift`,
      value: shift.id
    })) || []

  const eventOptions = [...visitOptions, ...shiftOptions]

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isVisible) {
      setFilters(initialFilters)
    }
  }, [isVisible, initialFilters])

  const handleFilterChange = (newValue: Partial<RotaOverviewFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newValue
    }))
  }

  const handleDownload = async () => {
    try {
      await exportCsv()
      onClose()
    } catch (error) {
      console.error('Failed to download CSV:', error)
    }
  }

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={
        <ModalFooter>
          <Button onClick={handleDownload} variant="primary">
            {t('DOWNLOAD_CSV')}
            <DownloadFileIcon />
          </Button>
        </ModalFooter>
      }
      destroyOnClose
    >
      <ModalTitle>
        <Heading level={3}>{t('ROTA_OVERVIEW_REPORT_DOWNLOAD')}</Heading>
        <StyledDivider />
      </ModalTitle>

      <ModalContent>
        <FormItem label={<Text>{t('DATE_RANGE_MODAL')}</Text>} required long>
          <WeekRangePicker
            value={[filters.from, filters.to]}
            onChange={(dates) =>
              handleFilterChange({
                from: dates[0],
                to: dates[1]
              })
            }
          />
        </FormItem>

        <FormItem label={<Text>{t('TEAM_MEMBERS')}</Text>} long>
          <GroupedUsersMultiSelect
            users={users}
            title={t('TEAM_MEMBERS')}
            values={filters.teamMembers}
            handleInputChange={(selectedOptions) =>
              handleFilterChange({
                teamMembers: selectedOptions as string[]
              })
            }
          />
        </FormItem>

        <FormItem label={<Text>{t('CONTRACT_TYPES')}</Text>} long>
          <StyledMultiSelect
            title={t('CONTRACT_TYPES')}
            options={contractTypeOptions}
            values={filters.contractTypes}
            hasClear
            hasSearch
            hasSelectAll
            onChange={(selectedTypes) =>
              handleFilterChange({
                contractTypes: selectedTypes as string[]
              })
            }
          />
        </FormItem>

        <FormItem
          label={<Text>{t('BREAKDOWN_BY_EVENT_TYPE')}</Text>}
          extra={t('BREAKDOWN_BY_EVENT_TYPE_DESCRIPTION')}
          long
        >
          <StyledMultiSelect
            title={t('TYPE_OF_EVENT')}
            options={eventOptions}
            values={filters.eventTypes}
            hasClear
            hasSearch
            hasSelectAll
            onChange={(selectedTypes) =>
              handleFilterChange({
                eventTypes: selectedTypes as string[]
              })
            }
          />
        </FormItem>
      </ModalContent>
      <StyledDivider />
    </Modal>
  )
}

const ModalTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const ModalContent = styled.div`
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledDivider = styled(Divider)`
  margin: 0;
`

const ModalFooter = styled.div`
  margin-top: 12px;
`

export default DownloadRotaOverviewModal
